import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Users, Wallet } from 'lucide-react';
import { useAssociates } from '../hooks/useAssociates.js';
import { useEntityScope } from '../hooks/useEntityScope.js';
import AssociateForm from '../components/AssociateForm.jsx';
import AssociateAccountForm from '../components/AssociateAccountForm.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
import { formatCurrency, formatDate } from '../lib/format.js';

// ── KPI Card ──────────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, accentClass = '' }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 shadow-sm px-4 py-4 flex flex-col gap-1">
      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
      <p className={`text-2xl font-bold tabular-nums ${accentClass || 'text-slate-900'}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const cfg = status === 'active'
    ? { label: 'Actif',   cls: 'bg-emerald-100 text-emerald-700' }
    : { label: 'Archivé', cls: 'bg-slate-100 text-slate-500' };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

// ── Account badge inside associate row ───────────────────────────────────────

function AccountBadge({ account, onClick }) {
  const balance = account.balance_cents ?? 0;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(account); }}
      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 hover:border-slate-300 transition-colors text-slate-700"
      aria-label={`Voir le compte de cette société`}
    >
      <span className="font-medium">{account.entity_name ?? 'Société'}</span>
      <span className={`tabular-nums font-semibold ${balance < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
        {formatCurrency(balance)}
      </span>
    </button>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function AssociatesPage() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useAssociates();
  const { isGroup, activeEntityName } = useEntityScope();
  const [associateFormOpen, setAssociateFormOpen]     = useState(false);
  const [accountFormOpen,   setAccountFormOpen]       = useState(false);
  const [accountFormAssociateId, setAccountFormAssociateId] = useState(null);

  const associates = Array.isArray(data) ? data : [];
  const active = associates.filter((a) => a.status === 'active');

  function openAccountForm(associateId) {
    setAccountFormAssociateId(associateId);
    setAccountFormOpen(true);
  }

  // Agrège tous les comptes de tous les associés pour les KPIs
  const allAccounts = associates.flatMap((a) => a.accounts ?? []);
  const openAccounts = allAccounts.filter((acc) => acc.status === 'active');
  const totalBalance = openAccounts.reduce((s, acc) => s + (acc.balance_cents ?? 0), 0);

  function handleAccountClick(account) {
    navigate(`/associes/comptes/${account.id}`);
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Associés</h1>
          <p className="text-sm text-slate-500">Comptes courants d'associés</p>
        </div>
        <button
          type="button"
          onClick={() => setAssociateFormOpen(true)}
          className="flex items-center gap-1.5 bg-slate-800 text-white text-sm font-medium px-3 py-2 rounded-lg hover:bg-slate-700 transition-colors shrink-0"
          aria-label="Créer un nouvel associé"
        >
          <Plus size={15} />
          <span className="hidden sm:inline">Nouvel associé</span>
          <span className="sm:hidden">Nouveau</span>
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          Impossible de charger les associés : {error.message}
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {!isLoading && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <KpiCard
              label="Associés actifs"
              value={active.length}
              sub={`${associates.length} au total`}
            />
            <KpiCard
              label="Comptes ouverts"
              value={openAccounts.length}
              sub="Toutes sociétés"
            />
            <KpiCard
              label="Solde total CCA"
              value={formatCurrency(totalBalance)}
              sub="Tous comptes ouverts"
              accentClass={totalBalance < 0 ? 'text-red-700' : 'text-slate-900'}
            />
          </div>

          {/* Empty state */}
          {associates.length === 0 && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 px-6 py-16 text-center">
              <Users size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm font-medium text-slate-700 mb-1">Aucun associé enregistré</p>
              <p className="text-sm text-slate-500 mb-4">
                Créez votre premier associé pour suivre les comptes courants.
              </p>
              <button
                type="button"
                onClick={() => setAssociateFormOpen(true)}
                className="inline-flex items-center gap-1.5 bg-slate-800 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-slate-700 transition-colors"
              >
                <Plus size={14} /> Nouvel associé
              </button>
            </div>
          )}

          {/* Table */}
          {associates.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left text-xs font-medium text-slate-500 px-4 py-3 sticky left-0 bg-slate-50 whitespace-nowrap">
                        Nom
                      </th>
                      <th className="text-left text-xs font-medium text-slate-500 px-4 py-3 whitespace-nowrap hidden sm:table-cell">
                        Email
                      </th>
                      <th className="text-left text-xs font-medium text-slate-500 px-4 py-3 whitespace-nowrap">
                        Comptes / Soldes
                      </th>
                      <th className="text-center text-xs font-medium text-slate-500 px-4 py-3 whitespace-nowrap">
                        Statut
                      </th>
                      <th className="text-xs font-medium text-slate-500 px-4 py-3 whitespace-nowrap hidden lg:table-cell">
                        Depuis
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {associates.map((a) => {
                      const accounts = a.accounts ?? [];
                      return (
                        <tr
                          key={a.id}
                          className="hover:bg-slate-50 transition-colors"
                        >
                          <td className="px-4 py-3 sticky left-0 bg-white font-medium text-slate-900 whitespace-nowrap">
                            {a.display_name ?? `${a.first_name} ${a.last_name}`}
                          </td>
                          <td className="px-4 py-3 text-slate-500 whitespace-nowrap hidden sm:table-cell">
                            {a.email ?? '—'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap items-center gap-1.5">
                              {accounts.length === 0 && (
                                <span className="text-xs text-slate-400">Aucun compte</span>
                              )}
                              {accounts.map((acc) => (
                                <AccountBadge
                                  key={acc.id}
                                  account={acc}
                                  onClick={handleAccountClick}
                                />
                              ))}
                              {!isGroup && a.status === 'active' && accounts.length === 0 && (
                                <button
                                  type="button"
                                  onClick={() => openAccountForm(a.id)}
                                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-dashed border-slate-300 text-slate-500 hover:bg-slate-50 hover:text-slate-700 hover:border-slate-400 transition-colors"
                                  aria-label={`Ouvrir un compte pour ${a.display_name}`}
                                >
                                  <Wallet size={12} />
                                  Ouvrir un compte sur {activeEntityName ?? 'cette société'}
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <StatusBadge status={a.status} />
                          </td>
                          <td className="px-4 py-3 text-slate-500 whitespace-nowrap hidden lg:table-cell">
                            {formatDate(a.created_at)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {isGroup && associates.length > 0 && (
        <p className="text-xs text-slate-500 italic">
          Pour ouvrir un nouveau compte courant, basculez sur une société depuis le menu en haut à gauche.
        </p>
      )}

      <AssociateForm
        open={associateFormOpen}
        onClose={() => setAssociateFormOpen(false)}
      />
      <AssociateAccountForm
        open={accountFormOpen}
        onClose={() => { setAccountFormOpen(false); setAccountFormAssociateId(null); }}
        defaultAssociateId={accountFormAssociateId}
      />
    </div>
  );
}
